<script>
/**
 * WhatsAppTemplatePreview.vue
 *
 * Renders a realistic WhatsApp message preview for template inspection.
 * Supports headers (text, image, video, document), body with variable
 * substitution, footer, and action/quick-reply buttons.
 *
 * Uses CSS custom properties for WhatsApp-specific colors so the light/dark
 * theme can be toggled via the darkMode prop without relying on Tailwind
 * dark: variant (which is controlled by a class on the HTML root, not a prop).
 */
export default {
  name: 'WhatsAppTemplatePreview',

  props: {
    headerType: {
      type: String,
      default: 'none',
      validator: v =>
        ['none', 'text', 'image', 'video', 'document'].includes(v),
    },
    headerContent: {
      type: String,
      default: '',
    },
    // Nome real do arquivo (p/ header de documento, quando o content é um objectURL).
    headerFilename: {
      type: String,
      default: '',
    },
    body: {
      type: String,
      default: '',
    },
    footer: {
      type: String,
      default: '',
    },
    buttons: {
      type: Array,
      default: () => [],
    },
    variables: {
      type: Object,
      default: () => ({}),
    },
    timestamp: {
      type: String,
      default: '12:00',
    },
    darkMode: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    themeVars() {
      if (this.darkMode) {
        return {
          '--wa-bg': '#0b141a',
          '--wa-bubble-bg': '#005c4b',
          '--wa-bubble-text': '#e9edef',
          '--wa-footer-text': '#8696a0',
          '--wa-btn-bg': '#1f2c34',
          '--wa-btn-text': '#00a884',
          '--wa-btn-border': '#2a3942',
          '--wa-header-media-bg': '#1f2c34',
          '--wa-doc-bg': '#1f2c34',
          '--wa-doc-icon': '#8696a0',
          '--wa-tick': '#53bdeb',
          '--wa-var-highlight': 'rgba(0,168,132,0.25)',
          '--wa-shadow': '0 1px 2px rgba(0,0,0,0.4)',
        };
      }
      return {
        '--wa-bg': '#e5ddd5',
        '--wa-bubble-bg': '#dcf8c6',
        '--wa-bubble-text': '#111b21',
        '--wa-footer-text': '#667781',
        '--wa-btn-bg': '#ffffff',
        '--wa-btn-text': '#0093ff',
        '--wa-btn-border': '#e9edef',
        '--wa-header-media-bg': '#ccd0d5',
        '--wa-doc-bg': '#f0f2f5',
        '--wa-doc-icon': '#8696a0',
        '--wa-tick': '#53bdeb',
        '--wa-var-highlight': 'rgba(0,100,210,0.15)',
        '--wa-shadow': '0 1px 2px rgba(0,0,0,0.15)',
      };
    },

    bodyTokens() {
      if (!this.body) return [];
      return this._tokenize(this.body);
    },

    hasButtons() {
      return this.buttons && this.buttons.length > 0;
    },

    hasHeader() {
      return this.headerType && this.headerType !== 'none';
    },

    documentFilename() {
      if (this.headerFilename) return this.headerFilename;
      if (!this.headerContent) return 'document';
      const parts = this.headerContent.split('/');
      return decodeURIComponent(parts[parts.length - 1]) || 'document';
    },
  },

  methods: {
    _tokenize(text) {
      const tokens = [];
      const lines = text.split('\n');
      lines.forEach((line, lineIdx) => {
        if (lineIdx > 0) tokens.push({ type: 'br' });
        const re = /(\{\{(\d+)\}\})|(\*([^*]+)\*)|(_([^_]+)_)/g;
        let lastIndex = 0;
        let match;
        while ((match = re.exec(line)) !== null) {
          if (match.index > lastIndex) {
            tokens.push({
              type: 'text',
              value: line.slice(lastIndex, match.index),
            });
          }
          if (match[1]) {
            const idx = match[2];
            const resolved =
              this.variables && this.variables[idx] != null
                ? String(this.variables[idx])
                : null;
            tokens.push({ type: 'var', index: idx, value: resolved });
          } else if (match[3]) {
            tokens.push({ type: 'bold', value: match[4] });
          } else if (match[5]) {
            tokens.push({ type: 'italic', value: match[6] });
          }
          lastIndex = re.lastIndex;
        }
        if (lastIndex < line.length) {
          tokens.push({ type: 'text', value: line.slice(lastIndex) });
        }
      });
      return tokens;
    },

    buttonIcon(type) {
      const map = {
        URL: 'i-ri-external-link-line',
        PHONE_NUMBER: 'i-ri-phone-line',
        QUICK_REPLY: 'i-ri-reply-line',
      };
      return map[type] || 'i-ri-reply-line';
    },
  },
};
</script>

<template>
  <div class="wa-preview-root" :style="themeVars">
    <div class="wa-chat-bg">
      <div class="wa-bubble-wrapper">
        <div
          class="wa-bubble"
          :class="{ 'wa-bubble--has-buttons': hasButtons }"
        >
          <!-- HEADER -->
          <div v-if="hasHeader" class="wa-header">
            <p v-if="headerType === 'text'" class="wa-header-text">
              {{ headerContent }}
            </p>
            <div v-else-if="headerType === 'image'" class="wa-header-media">
              <img
                v-if="headerContent"
                :src="headerContent"
                alt="Header image"
                class="wa-header-img"
              />
              <div v-else class="wa-header-media-placeholder">
                <span class="i-ri-image-line size-8 wa-placeholder-icon" />
              </div>
            </div>
            <div v-else-if="headerType === 'video'" class="wa-header-media">
              <video
                v-if="headerContent"
                :src="headerContent"
                class="wa-header-img"
                preload="none"
              />
              <div v-else class="wa-header-media-placeholder">
                <span class="i-ri-video-line size-8 wa-placeholder-icon" />
              </div>
              <div class="wa-video-play-overlay">
                <span class="i-ri-play-circle-line size-10 wa-play-icon" />
              </div>
            </div>
            <div v-else-if="headerType === 'document'" class="wa-header-doc">
              <span class="i-ri-file-text-line size-6 wa-doc-icon" />
              <span class="wa-doc-filename">{{ documentFilename }}</span>
            </div>
          </div>

          <!-- BODY -->
          <div v-if="body" class="wa-body">
            <template v-for="(token, idx) in bodyTokens" :key="idx">
              <br v-if="token.type === 'br'" />
              <strong v-else-if="token.type === 'bold'">{{
                token.value
              }}</strong>
              <em v-else-if="token.type === 'italic'">{{ token.value }}</em>
              <span
                v-else-if="token.type === 'var' && token.value !== null"
                class="wa-var-filled"
                >{{ token.value }}</span>
              <span
                v-else-if="token.type === 'var' && token.value === null"
                class="wa-var-empty"
                v-text="'{{' + token.index + '}}'"
              />
              <span v-else>{{ token.value }}</span>
            </template>
          </div>

          <!-- FOOTER -->
          <p v-if="footer" class="wa-footer">{{ footer }}</p>

          <!-- META -->
          <div class="wa-meta">
            <span class="wa-timestamp">{{ timestamp }}</span>
            <span class="wa-ticks" aria-hidden="true">
              <svg
                width="16"
                height="11"
                viewBox="0 0 16 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5.5L4.5 9L10 3"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M5 5.5L8.5 9L14 3"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </div>

          <div class="wa-tail" aria-hidden="true" />
        </div>

        <!-- BUTTONS -->
        <div v-if="hasButtons" class="wa-buttons-wrapper">
          <div v-for="(btn, idx) in buttons" :key="idx" class="wa-button">
            <span class="size-4 wa-btn-icon" :class="[buttonIcon(btn.type)]" />
            <span class="wa-btn-text">{{ btn.text }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wa-preview-root {
  font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
  min-width: 180px;
  max-width: 320px;
  width: 100%;
}

.wa-chat-bg {
  background-color: var(--wa-bg);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' opacity='0.06'%3E%3Ccircle cx='30' cy='30' r='20' fill='none' stroke='%23000' stroke-width='1.5'/%3E%3Ccircle cx='30' cy='30' r='10' fill='none' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E");
  border-radius: 12px;
  padding: 12px 8px;
}

.wa-bubble-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.wa-bubble {
  position: relative;
  background-color: var(--wa-bubble-bg);
  color: var(--wa-bubble-text);
  border-radius: 8px 0 8px 8px;
  box-shadow: var(--wa-shadow);
  max-width: 100%;
  min-width: 120px;
  overflow: hidden;
}

.wa-bubble--has-buttons {
  border-radius: 8px 0 0 0;
}

.wa-tail {
  position: absolute;
  top: 0;
  right: -8px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 8px 0 0 8px;
  border-color: transparent transparent transparent var(--wa-bubble-bg);
}

.wa-header-text {
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  padding: 8px 10px 4px;
  color: var(--wa-bubble-text);
}

.wa-header-media {
  position: relative;
  width: 100%;
  background-color: var(--wa-header-media-bg);
  overflow: hidden;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wa-header-img {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.wa-header-media-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 120px;
}

.wa-placeholder-icon {
  color: var(--wa-doc-icon);
}

.wa-video-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
}

.wa-play-icon {
  color: #ffffff;
  opacity: 0.9;
}

.wa-header-doc {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--wa-doc-bg);
  border-radius: 6px;
  margin: 8px 10px 4px;
  padding: 10px 12px;
}

.wa-doc-icon {
  color: var(--wa-doc-icon);
  flex-shrink: 0;
}

.wa-doc-filename {
  font-size: 0.8rem;
  color: var(--wa-bubble-text);
  word-break: break-all;
  line-height: 1.3;
}

.wa-body {
  padding: 8px 10px 2px;
  font-size: 0.875rem;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
}

.wa-var-filled {
  font-weight: 500;
}

.wa-var-empty {
  background-color: var(--wa-var-highlight);
  border-radius: 3px;
  padding: 0 3px;
  font-size: 0.8em;
  font-family: monospace;
  color: var(--wa-bubble-text);
  opacity: 0.85;
}

.wa-footer {
  padding: 2px 10px 2px;
  font-size: 0.75rem;
  color: var(--wa-footer-text);
  margin: 0;
  line-height: 1.4;
}

.wa-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  padding: 2px 8px 6px;
  line-height: 1;
}

.wa-timestamp {
  font-size: 0.65rem;
  color: var(--wa-footer-text);
  white-space: nowrap;
}

.wa-ticks {
  color: var(--wa-tick);
  display: flex;
  align-items: center;
}

.wa-ticks svg {
  display: block;
}

.wa-buttons-wrapper {
  width: 100%;
  background-color: var(--wa-btn-bg);
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  box-shadow: var(--wa-shadow);
  margin-top: 1px;
}

.wa-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid var(--wa-btn-border);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--wa-btn-text);
  cursor: default;
  user-select: none;
}

.wa-button:first-child {
  border-top: none;
}

.wa-btn-icon {
  color: var(--wa-btn-text);
  flex-shrink: 0;
}

.wa-btn-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
